// TWA Popup Checker Custom Element
class TWAPopupChecker extends HTMLElement {
    constructor() {
        super();
        this.popup = this;
        this.overlay = this.querySelector('.twa-popup-container__overlay');
        this.closeBtn = this.querySelector('.twa-popup-container__close');

        this.countryCode = this.dataset.countryCode || '';
        this.popupDays = parseInt(this.dataset.popupDays) || 7;
        this.isEditor = this.dataset.iseditor === 'true';
        this.cookieName = 'twa_popup_dismissed';

        // Bind methods
        this.handleClose = this.handleClose.bind(this);
        this.handleOverlayClick = this.handleOverlayClick.bind(this);
        this.handleEscapeKey = this.handleEscapeKey.bind(this);
        this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this);
    }

    connectedCallback() {
        // Always show in theme editor
        if (this.isEditor) {
            this.showPopup();
            this.bindEvents();
            return;
        }

        // Check if already dismissed
        if (this.getCookie(this.cookieName)) {
            this.hidePopup();
            return;
        }

        // Check user's country
        this.checkCountry();
        this.bindEvents();
    }

    bindEvents() {
        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', this.handleClose);
        }
        if (this.overlay) {
            this.overlay.addEventListener('click', this.handleOverlayClick);
        }
        // Close on escape key
        document.addEventListener('keydown', this.handleEscapeKey);

        // Find all buttons/links with id="closepopup" inside the popup
        this.closePopupButtons = this.querySelectorAll('#closepopup, [data-close-popup]');
        this.closePopupButtons.forEach(btn => {
            btn.addEventListener('click', this.handleCloseButtonClick);
        });
    }

    handleCloseButtonClick(event) {
        const target = event.currentTarget;
        const href = target.getAttribute('href');

        // If it has a valid href (not empty, not just "#"), redirect after closing
        if (href && href !== '' && href !== '#') {
            event.preventDefault();
            this.handleClose();
            // Small delay to allow cookie to be set before redirect
            setTimeout(() => {
                window.location.href = href;
            }, 100);
        } else {
            // Just close the popup
            event.preventDefault();
            this.handleClose();
        }
    }

    handleEscapeKey(e) {
        if (e.key === 'Escape') {
            this.handleClose();
        }
    }

    async checkCountry() {
        try {
            const response = await fetch('/browsing_context_suggestions.json');
            const data = await response.json();
            const userCountry = data.detected_values?.country?.handle?.toUpperCase() || '';

            // Parse excluded country codes
            const excludedCountries = this.countryCode
                .split(',')
                .map(code => code.trim().toUpperCase());

            // Hide popup if user is in excluded country
            if (excludedCountries.includes(userCountry)) {
                this.hidePopup();
            } else {
                this.showPopup();
            }
        } catch (error) {
            console.warn('Could not detect country:', error);
            // Show popup by default if country detection fails
            this.showPopup();
        }
    }

    showPopup() {
        if (this.popup) {
            this.popup.classList.add('is-visible');
            if(!this.isEditor) {
                document.body.style.overflow = 'hidden';
            }
        }
    }

    hidePopup() {
        if (this.popup) {
            this.popup.classList.remove('is-visible');
            document.body.style.overflow = '';
        }
    }

    handleClose() {
        this.hidePopup();
        // Set cookie to not show again for X days
        if (!this.isEditor) {
            this.setCookie(this.cookieName, 'true', this.popupDays);
        }
    }

    handleOverlayClick(event) {
        if (event.target === this.overlay) {
            this.handleClose();
        }
    }

    setCookie(name, value, days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = 'expires=' + date.toUTCString();
        document.cookie = name + '=' + value + ';' + expires + ';path=/';
    }

    getCookie(name) {
        const cookieName = name + '=';
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i].trim();
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return null;
    }

    disconnectedCallback() {
        if (this.closeBtn) {
            this.closeBtn.removeEventListener('click', this.handleClose);
        }
        if (this.overlay) {
            this.overlay.removeEventListener('click', this.handleOverlayClick);
        }
        document.removeEventListener('keydown', this.handleEscapeKey);

        // Remove close popup button listeners
        if (this.closePopupButtons) {
            this.closePopupButtons.forEach(btn => {
                btn.removeEventListener('click', this.handleCloseButtonClick);
            });
        }
    }
}

if (!customElements.get('twa-popup-checker')) {
    customElements.define('twa-popup-checker', TWAPopupChecker);
}
